/**
 * Split-Kennzeichnung (Auftrag "Split-Kennzeichnung: Frontend"): wie haengt eine Bestellung an einer
 * Split-Kette? Reine Funktionen, KEIN Zustand - die Listen bauen den Index einmal (`useMemo`), nicht
 * je Zeile.
 *
 * Datenlage im Listen-Payload: `splitFrom` (uuid der direkten Quelle), `splitRoot` (uuid der Wurzel)
 * und die Bestellnummern dazu (`splitFromOrderNo`, `splitRootOrderNo`). `splitChildren` ist eine tote
 * Spalte und wird NICHT gelesen; die Teile einer Quelle kommen aus dem Index ueber `splitFrom` der
 * GELADENEN Bestellungen. Sammellieferung (`mergedInto`) gehoert nicht hierher.
 *
 * `status.ts#isSplitCollectionOrder` bleibt unberuehrt (modulintern, andere Frage: "ist Split ODER
 * Quelle", ohne Nummern).
 */
import type { Order } from "./types.ts";

export interface SplitRef {
  id: string;
  nummer: string;
}

export type SplitBezug =
  | { rolle: "teil"; ursprung: SplitRef; wurzel?: SplitRef }
  /** Mittelglied einer Kette (Split vom Split): selbst Teil UND Quelle weiterer Teile. */
  | { rolle: "beides"; ursprung: SplitRef; wurzel?: SplitRef; teile: SplitRef[] }
  | { rolle: "aufgeteilt"; teile: SplitRef[] };

/** quelleId -> Bestellungen, die per `splitFrom` darauf zeigen. */
export type SplitKinderIndex = ReadonlyMap<string, Order[]>;

function text(wert: unknown): string {
  return typeof wert === "string" ? wert : "";
}

export function splitKinderIndex(alle: readonly Order[]): Map<string, Order[]> {
  const index = new Map<string, Order[]>();
  for (const bestellung of alle) {
    const quelle = text(bestellung.splitFrom);
    if (!quelle) continue;
    const liste = index.get(quelle);
    if (liste) liste.push(bestellung);
    else index.set(quelle, [bestellung]);
  }
  return index;
}

/** Id -> Bestellung, fuer den Nummern-Rueckfall (Quelle steht im Store, hat aber keine Payload-Nummer). */
export function bestellungenNachId(alle: readonly Order[]): Map<string, Order> {
  const map = new Map<string, Order>();
  for (const bestellung of alle) map.set(String(bestellung.id), bestellung);
  return map;
}

function gekuerzt(id: string): string {
  return id.slice(0, 8);
}

function nummerAus(id: string, ausPayload: unknown, nachId?: ReadonlyMap<string, Order>): string {
  const direkt = text(ausPayload).trim();
  if (direkt) return direkt;
  const imStore = text(nachId?.get(id)?.orderNo).trim();
  return imStore || gekuerzt(id);
}

export function splitBezug(
  order: Order,
  index: SplitKinderIndex,
  nachId?: ReadonlyMap<string, Order>,
): SplitBezug | null {
  const id = String(order.id);
  const quelleId = text(order.splitFrom);
  const wurzelId = text(order.splitRoot);
  const teile: SplitRef[] = (index.get(id) ?? []).map((kind) => ({
    id: String(kind.id),
    nummer: text(kind.orderNo).trim() || gekuerzt(String(kind.id)),
  }));

  if (!quelleId) return teile.length ? { rolle: "aufgeteilt", teile } : null;

  const ursprung: SplitRef = { id: quelleId, nummer: nummerAus(quelleId, order.splitFromOrderNo, nachId) };
  const wurzel: SplitRef | undefined =
    wurzelId && wurzelId !== quelleId
      ? { id: wurzelId, nummer: nummerAus(wurzelId, order.splitRootOrderNo, nachId) }
      : undefined;
  const teil = wurzel ? { ursprung, wurzel } : { ursprung };
  return teile.length ? { rolle: "beides", ...teil, teile } : { rolle: "teil", ...teil };
}
